import { KeyboardEventHandler, useEffect, useRef, useState } from "react";
import { ScanSearch, Wand2, CheckCircle2, Loader2, Eye } from "lucide-react";

import { Textarea, Label, MultiSelect } from "@/components/atoms";
import { Widget } from "@/modules";
import { EditClarificationItem } from "@/modules/websocket/types";

function StepIcon({ step, isActive }: { step: string; isActive: boolean }) {
    if (isActive) return <Loader2 className="mt-0.5 h-3.5 w-3.5 shrink-0 animate-spin text-[#079a95]" />;
    if (/analyz/i.test(step))  return <ScanSearch  className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#079a95]" />;
    if (/apply/i.test(step))   return <Wand2        className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#079a95]" />;
    if (/finaliz/i.test(step)) return <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#079a95]" />;
    return <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#079a95]" />;
}

interface EditViaAIPanelProps {
    prompt: string;
    setPrompt: (value: string) => void;
    onSubmit: () => void;
    isProcessing: boolean;
    isDisabled: boolean;
    progressSteps: string[];
    feedbackMessage?: string;
    onKeyDown: KeyboardEventHandler<HTMLTextAreaElement | HTMLInputElement>;
    previewWidget?: Widget | null;
    onPreviewDiscard?: () => void;
    onPreviewOpen?: () => void;
    onRefineSubmit?: (refineText: string) => void;
    clarificationItems?: EditClarificationItem[];
    onClarificationConfirm?: (answers: { title: string; values: string[] }[]) => void;
}

export function EditViaAIPanel({
    prompt,
    setPrompt,
    isProcessing,
    progressSteps,
    feedbackMessage,
    onKeyDown,
    previewWidget,
    onPreviewDiscard,
    onPreviewOpen,
    onRefineSubmit,
    clarificationItems,
    onClarificationConfirm,
}: EditViaAIPanelProps) {
    const allSteps = ["Analyzing widget configuration...", ...progressSteps];
    const scrollRef = useRef<HTMLDivElement>(null);
    const [itemSelections, setItemSelections] = useState<string[][]>([]);
    const [isRefineMode, setIsRefineMode] = useState(false);
    const [inlineRefineValue, setInlineRefineValue] = useState("");

    useEffect(() => {
        if (clarificationItems) {
            setItemSelections(clarificationItems.map((item) => [...item.preselected]));
        } else {
            setItemSelections([]);
        }
    }, [clarificationItems]);

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [allSteps.length]);

    useEffect(() => {
        if (!previewWidget) {
            setIsRefineMode(false);
            setInlineRefineValue("");
        }
    }, [previewWidget]);

    return (
        <div className="space-y-6">
            <section className="rounded-[24px] bg-white">
                <Label text="Describe the changes you want">
                    <Textarea
                        value={prompt}
                        onChange={(event) => setPrompt(event.target.value)}
                        onKeyDown={onKeyDown}
                        placeholder="E.g., change to bar chart, add clicks metric, show last 30 days..."
                        disabled={isProcessing || !!previewWidget || !!clarificationItems?.length}
                    />
                </Label>

                {feedbackMessage && !clarificationItems?.length && (
                    <div className="mt-4 rounded-xl border border-[#d14343]/30 bg-[#d14343]/5 px-4 py-3">
                        <p className="text-sm font-medium text-[#d14343]">{feedbackMessage}</p>
                    </div>
                )}
            </section>

            {/* ── Clarification questions ── */}
            {clarificationItems && clarificationItems.length > 0 && (
                <section className="rounded-xl border border-[#079a95]/30 bg-[#079a95]/5 px-4 py-3 space-y-4">
                    <span className="block text-xs font-semibold uppercase tracking-wide text-[#079a95]">
                        A few questions
                    </span>
                    {clarificationItems.map((item, idx) => {
                        const selected = itemSelections[idx] ?? item.preselected;
                        return (
                            <div key={item.title} className="space-y-1.5">
                                <p className="text-xs font-semibold text-[#2d3a3f]">{item.title}</p>
                                <p className="text-xs text-[#5a717a] whitespace-pre-line">{item.message}</p>
                                {item.freeText ? (
                                    <textarea
                                        rows={3}
                                        placeholder="Describe how you'd like to adjust your request..."
                                        value={selected[0] ?? ""}
                                        onChange={(e) =>
                                            setItemSelections((prev) => {
                                                const next = [...prev];
                                                next[idx] = [e.target.value];
                                                return next;
                                            })
                                        }
                                        className="block w-full appearance-none rounded-md border-0 bg-[#f3f3f5] px-2.5 py-1.5 text-sm text-gray-900 shadow-xs outline-hidden placeholder-gray-400 transition focus:border-[#079a95] focus:ring-2 focus:ring-[#079a95]/20"
                                    />
                                ) : item.options.length > 0 && (
                                    item.multi ? (
                                        <MultiSelect
                                            options={item.options.map((o, i) => ({ id: i, label: o }))}
                                            selectedIds={selected.map((s) => item.options.indexOf(s)).filter((i) => i >= 0)}
                                            onChange={(ids) => {
                                                const vals = ids.map((i) => item.options[i]).filter(Boolean);
                                                setItemSelections((prev) => {
                                                    const next = [...prev];
                                                    next[idx] = vals;
                                                    return next;
                                                });
                                            }}
                                            placeholder={`Select ${item.title.toLowerCase()}...`}
                                            searchable
                                            showSelectAll={false}
                                        />
                                    ) : (
                                        <div className="flex flex-wrap gap-2">
                                            {item.options.map((option) => (
                                                <button
                                                    key={option}
                                                    type="button"
                                                    onClick={() =>
                                                        setItemSelections((prev) => {
                                                            const next = [...prev];
                                                            next[idx] = [option];
                                                            return next;
                                                        })
                                                    }
                                                    className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors active:scale-95 ${
                                                        selected.includes(option)
                                                            ? "border-[#079a95] bg-[#079a95] text-white"
                                                            : "border-[#079a95] bg-white text-[#079a95] hover:bg-[#079a95] hover:text-white"
                                                    }`}
                                                >
                                                    {option}
                                                </button>
                                            ))}
                                        </div>
                                    )
                                )}
                            </div>
                        );
                    })}
                    <button
                        type="button"
                        disabled={clarificationItems.some((item, idx) => {
                            const sel = itemSelections[idx] ?? item.preselected;
                            if (item.freeText) return (sel[0] ?? "").trim() === "";
                            if (!item.multi) return sel.length === 0;
                            return false;
                        })}
                        onClick={() => {
                            const answers = clarificationItems.map((item, idx) => {
                                const selectedLabels = itemSelections[idx] ?? item.preselected;
                                const values = item.freeText
                                    ? [(selectedLabels[0] ?? "").trim()].filter(Boolean)
                                    : item.optionValues
                                        ? selectedLabels.map((label) => {
                                            const labelIdx = item.options.indexOf(label);
                                            return labelIdx >= 0 ? (item.optionValues![labelIdx] ?? label) : label;
                                        })
                                        : selectedLabels;
                                return { title: item.title, values };
                            });
                            onClarificationConfirm?.(answers);
                        }}
                        className="w-full rounded-lg bg-[#079a95] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#067a76] disabled:cursor-not-allowed disabled:opacity-40"
                    >
                        Continue
                    </button>
                </section>
            )}

            {previewWidget && (
                <section className="rounded-xl border border-[#079a95]/30 bg-[#e8f5f4] px-4 py-3 space-y-3">
                    <div className="flex items-center gap-1.5">
                        <Eye className="h-3.5 w-3.5 text-[#079a95]" />
                        <span className="text-xs font-semibold uppercase tracking-wide text-[#079a95]">Preview</span>
                    </div>
                    {isRefineMode ? (
                        <>
                            <Textarea
                                value={inlineRefineValue}
                                onChange={(e) => setInlineRefineValue(e.target.value)}
                                placeholder="E.g. change to monthly, add clicks metric…"
                                disabled={isProcessing}
                                className="min-h-[80px]"
                            />
                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    onClick={() => { setIsRefineMode(false); setInlineRefineValue(""); }}
                                    className="cursor-pointer rounded-lg border border-[#c9d1d4] bg-white px-3 py-1.5 text-xs font-medium text-[#2d3a3f] transition-colors hover:bg-[#f5f7f8]"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    disabled={!inlineRefineValue.trim() || isProcessing}
                                    onClick={() => { if (inlineRefineValue.trim()) onRefineSubmit?.(inlineRefineValue.trim()); }}
                                    className="flex-1 cursor-pointer rounded-lg bg-[#079a95] px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-[#067a76] disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                    Refine
                                </button>
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="space-y-0.5">
                                <p className="font-medium text-[#2d3a3f]">{previewWidget.title}</p>
                                <p className="text-sm text-[#5a717a]">
                                    {previewWidget.type}{(previewWidget as any).chartType ? ` / ${(previewWidget as any).chartType}` : ""}
                                    {(previewWidget as any).service?.parameters?.level ? ` · ${(previewWidget as any).service.parameters.level} level` : ""}
                                </p>
                                {(previewWidget as any).metrics?.length > 0 && (
                                    <p className="text-xs text-[#5a717a]">
                                        Metrics: {(previewWidget as any).metrics.slice(0, 4).join(", ")}
                                        {(previewWidget as any).metrics.length > 4 ? ` +${(previewWidget as any).metrics.length - 4} more` : ""}
                                    </p>
                                )}
                            </div>
                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    onClick={() => setIsRefineMode(true)}
                                    className="cursor-pointer rounded-lg border border-[#c9d1d4] bg-white px-3 py-1.5 text-xs font-medium text-[#2d3a3f] transition-colors hover:bg-[#f5f7f8]"
                                >
                                    Refine
                                </button>
                                <button
                                    type="button"
                                    onClick={onPreviewOpen}
                                    className="flex-1 cursor-pointer rounded-lg border border-[#079a95] bg-white py-1.5 text-xs font-medium text-[#079a95] transition hover:bg-[#e8f5f4]"
                                >
                                    Preview
                                </button>
                            </div>
                        </>
                    )}
                </section>
            )}

            {isProcessing && (
                <section className="rounded border border-[#e0e0e0] bg-[#f5f7f8] p-4">
                    <div className="text-[12px] font-medium text-[#2d3a3f]">Applying your changes</div>
                    <div ref={scrollRef} className="mt-3 max-h-48 space-y-2.5 overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                        {allSteps.map((step, index) => {
                            const isActive = index === allSteps.length - 1;
                            return (
                                <div key={`${step}-${index}`} className={`flex items-start gap-2 text-[12px] transition-opacity ${isActive ? "opacity-100" : "opacity-60"}`}>
                                    <StepIcon step={step} isActive={isActive} />
                                    <span className={isActive ? "font-medium text-[#2d3a3f]" : "text-[#5a717a]"}>
                                        {step}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </section>
            )}
        </div>
    );
}
